/* ReactionSketcher.jsx
 *
 * INLINE panel for DRAWING a custom reaction for rxn_enum (an alternative to typing
 * a SMIRKS). Rendered inside CompoundGenerationTab -- NOT as a ModalManager modal:
 * that manager shows one modal at a time, so opening it would replace/unmount the
 * compound-generation dialog and drop the result. Inline keeps the tab mounted, so
 * onAccept's setState lands correctly.
 *
 * The drawn reaction is extracted as an MDL RXN molfile via Ketcher's getMolfile();
 * the rxn_enum backend converts it to a reaction with RDKit (ReactionFromRxnBlock ->
 * ReactionToSmarts) and enumerates it over the building blocks, exactly like
 * --custom-smirks.
 *
 * IMPORTANT -- manual atom mapping is REQUIRED. Ketcher's Auto-Mapping needs the
 * Indigo server (unavailable here), and an UNMAPPED drawing makes RDKit emit the
 * product template as a constant (garbage, building blocks ignored). The user must
 * map every atom that appears on BOTH sides and leave leaving groups unmapped.
 * We warn (client-side) if no atom maps are present before submit.
 *
 * Lives in proto: src/ReactionSketcher.jsx (next to Ketcher.jsx / Sketcher.jsx).
 */
import React, { useRef, useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { Ketcher } from './Ketcher';
import { DialogSubmitButton, DialogCancelButton } from './ui/common/DialogActionButtons';

/**
 * True if the RXN molfile carries at least one atom-atom map. In a V2000 atom
 * line the atom-atom mapping is the 10th integer field after the element symbol.
 */
export function hasAtomMaps(rxnBlock) {
    const atomLine = /^\s*-?\d+\.\d+\s+-?\d+\.\d+\s+-?\d+\.\d+\s+\S+\s+((?:-?\d+\s+){11}-?\d+)/gm;
    let m = atomLine.exec(rxnBlock);
    while (m !== null) {
        const fields = m[1].trim().split(/\s+/).map(Number);
        if (fields[9]) return true; // mmm = atom-atom mapping, non-zero => mapped
        m = atomLine.exec(rxnBlock);
    }
    return false;
}

export function ReactionSketcher({ onAccept, onClose }) {
    const ketcherRef = useRef(null);
    const [name, setName] = useState('');
    const [warn, setWarn] = useState('');
    const [override, setOverride] = useState(false);

    async function handleAdd() {
        const rxn = await ketcherRef.current?.getReactionMolfile();
        if (!rxn || !rxn.includes('$RXN')) {
            setWarn('Draw a reaction first: two or more structures joined by a reaction arrow.');
            return;
        }
        if (!hasAtomMaps(rxn) && !override) {
            setWarn('No atom maps found. Use the reaction mapping tool to number every atom that'
                + ' appears on both sides (leave leaving groups such as -OH unmapped), or the'
                + ' reaction ignores your building blocks and produces garbage. Click "Add" again'
                + ' to override.');
            setOverride(true);
            return;
        }
        onAccept(rxn, name.trim() || undefined);
        onClose();
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '460px',
                gap: 1,
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
            }}
        >
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Draw the reacting core; use generic atoms / R-groups for parts that should vary.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Map every atom that appears on both sides; leave leaving groups (-OH) unmapped.
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Auto-mapping is unavailable here, so mapping must be done by hand.
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <Ketcher ref={ketcherRef} reactionMode iframeId="rxn-ketcher" />
            </Box>
            {warn && (
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                    {warn}
                </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                    size="small"
                    label="Reaction name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ width: '40%' }}
                />
                <Box sx={{ flex: 1 }} />
                <DialogCancelButton onClick={onClose}>
                    Cancel
                </DialogCancelButton>
                <DialogSubmitButton onClick={handleAdd}>
                    {override ? 'Add anyway' : 'Add to job'}
                </DialogSubmitButton>
            </Box>
        </Box>
    );
}
